/**
 * Super Admin — Professionals (list + verification queue + drawer + AI tasks).
 */
import { useEffect, useState, useCallback } from "react";
import { Search, X, UserCheck, UserX } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import {
  Card, TableShell, Th, Td, FilterPills, SummaryTiles, Modal,
  PrimaryBtn, GhostBtn, DangerBtn, StatusBadge, inputStyle, fieldLabel, useDebouncedValue, EmptyState, relTime,
} from "./_atoms";

const ROLES = [
  { value: "", label: "All" },
  { value: "trainer", label: "Trainers" },
  { value: "nutritionist", label: "Nutritionists" },
];
const STATUSES = [
  { value: "", label: "Any status" },
  { value: "verified", label: "Verified" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
  { value: "suspended", label: "Suspended" },
];

export default function Professionals({ initialStatus = "" }) {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const dq = useDebouncedValue(q, 300);
  const [role, setRole] = useState("");
  const [status, setStatus] = useState(initialStatus);
  const [drawer, setDrawer] = useState(null);

  const load = useCallback(() => {
    apiClient.get("/superadmin/professionals", { params: { q: dq || undefined, role: role || undefined, status: status || undefined, page: 1, size: 100 } })
      .then((r) => { setRows(r.data?.professionals || []); setTotal(r.data?.total || 0); });
  }, [dq, role, status]);
  useEffect(() => { apiClient.get("/superadmin/professionals/summary").then((r) => setSummary(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="superadmin-professionals">
      <SummaryTiles tiles={[
        { label: "Verified", value: summary?.verified },
        { label: "Pending", value: summary?.pending },
        { label: "Rejected", value: summary?.rejected },
        { label: "Active this week", value: summary?.active_this_week },
      ]} />
      <SectionHeader title={initialStatus === "pending" ? "Verification Queue" : "Professionals"} onRefresh={load} />
      <Card style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 240px" }}>
          <Search size={14} style={{ position: "absolute", top: 15, left: 14, color: "var(--text-tertiary, #8A8884)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, city…"
            style={{ ...inputStyle, paddingLeft: 36 }} data-testid="pro-search" />
        </div>
        <FilterPills value={role} onChange={setRole} options={ROLES} testid="pro-role-filter" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ ...inputStyle, width: 180 }} data-testid="pro-status-filter">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </Card>

      {rows.length === 0 ? <EmptyState title="No professionals match" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="pros-table">
            <thead>
              <tr><Th>Name</Th><Th>Role</Th><Th>City</Th><Th>Rate</Th><Th>Status</Th><Th>Joined</Th></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.professional_id} onClick={() => setDrawer(p)} style={{ cursor: "pointer" }} data-testid={`pro-row-${p.professional_id}`}>
                  <Td><span style={{ color: "var(--text-primary, #0C0C0B)", fontWeight: 600 }}>{p.full_name || p.name}</span></Td>
                  <Td>{p.role}</Td>
                  <Td>{p.city || "—"}</Td>
                  <Td>{p.rate_per_session_inr ? `₹${p.rate_per_session_inr}` : "—"}</Td>
                  <Td><StatusBadge value={p.status} /></Td>
                  <Td>{(p.created_at || "").slice(0, 10)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>Showing {rows.length} of {total}</div>

      {drawer && <ProDrawer pro={drawer} onClose={() => setDrawer(null)} onChanged={load} />}
    </div>
  );
}

export function PendingProfessionals() {
  return <Professionals initialStatus="pending" />;
}

function ProDrawer({ pro, onClose, onChanged }) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(null); // verify | reject | suspend | reinstate

  const act = async () => {
    try {
      const pid = pro.application_id || pro.professional_id;
      if (!pid) return toast.error("Missing application id");
      if (open === "verify") await apiClient.post(`/superadmin/professionals/${pid}/verify`);
      if (open === "reject") {
        if (!reason || reason.length < 5) return toast.error("Reason required");
        await apiClient.post(`/superadmin/professionals/${pid}/reject`, { reason });
      }
      if (open === "suspend") {
        if (!reason || reason.length < 5) return toast.error("Reason required");
        await apiClient.post(`/superadmin/professionals/${pid}/suspend`, { reason });
      }
      if (open === "reinstate") await apiClient.post(`/superadmin/professionals/${pid}/reinstate`);
      toast.success("Updated");
      setOpen(null); setReason(""); onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, width: "min(640px, 100vw)", zIndex: 70,
      background: "var(--bg-surface, #E4E2DE)", borderLeft: "1px solid var(--border, rgba(12,12,11,0.09))",
      display: "flex", flexDirection: "column", overflowY: "auto",
    }} data-testid="pro-drawer">
      <div style={{ padding: 20, borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>{pro.full_name || pro.name}</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>{pro.role} · {pro.city || "—"} · {pro.application_id || pro.professional_id}</div>
        </div>
        <button onClick={onClose} aria-label="Close" data-testid="pro-drawer-close" style={{ background: "transparent" }}><X size={18} /></button>
      </div>
      <div style={{ padding: 20, display: "grid", gap: 12 }}>
        <Card>
          <Row label="Status" value={<StatusBadge value={pro.status} />} />
          <Row label="Cert body" value={pro.cert_body || "—"} />
          <Row label="Cert number" value={pro.cert_number || "—"} />
          <Row label="Years experience" value={pro.years_experience ?? "—"} />
          <Row label="Rate / session" value={pro.rate_per_session_inr ? `₹${pro.rate_per_session_inr}` : "—"} />
          <Row label="Specialisations" value={(pro.specialisations || []).join(", ") || "—"} />
          <Row label="Languages" value={(pro.languages || []).join(", ") || "—"} />
          <Row label="Bio" value={<span style={{ display: "block", fontSize: 13 }}>{pro.bio || "—"}</span>} />
          {pro.rejection_reason && <Row label="Rejection reason" value={pro.rejection_reason} />}
          {pro.suspension_reason && <Row label="Suspension reason" value={pro.suspension_reason} />}
        </Card>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {pro.status !== "verified" && <PrimaryBtn onClick={() => setOpen("verify")} data-testid="pro-verify-btn"><UserCheck size={14} /> Verify</PrimaryBtn>}
          {pro.status !== "rejected" && <DangerBtn onClick={() => setOpen("reject")} data-testid="pro-reject-btn"><UserX size={14} /> Reject</DangerBtn>}
          {pro.status === "verified" && <DangerBtn onClick={() => setOpen("suspend")} data-testid="pro-suspend-btn">Suspend</DangerBtn>}
          {pro.status === "suspended" && <PrimaryBtn onClick={() => setOpen("reinstate")} data-testid="pro-reinstate-btn">Reinstate</PrimaryBtn>}
        </div>
      </div>

      <Modal open={!!open} onClose={() => setOpen(null)} title={`${open?.charAt(0).toUpperCase()}${open?.slice(1)} ${pro.full_name || ""}`}>
        {(open === "reject" || open === "suspend") && (
          <>
            <label style={fieldLabel}>Reason (≥5 chars)</label>
            <textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              style={{ ...inputStyle, height: 100, padding: "10px 14px" }}
              data-testid="pro-action-reason"
            />
          </>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <GhostBtn onClick={() => setOpen(null)}>Cancel</GhostBtn>
          <PrimaryBtn onClick={act} data-testid="pro-action-confirm">Confirm</PrimaryBtn>
        </div>
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, gap: 12 }}>
      <span style={{ color: "var(--text-tertiary, #8A8884)" }}>{label}</span>
      <span style={{ color: "var(--text-primary, #0C0C0B)", maxWidth: "60%", textAlign: "right" }}>{value}</span>
    </div>
  );
}

export function AITrainerTasks() {
  const [data, setData] = useState({ tasks: [], total: 0 });
  const load = useCallback(() => {
    apiClient.get("/superadmin/ai-trainer-tasks").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="ai-trainer-tasks">
      <SectionHeader title="AI Trainer Tasks" onRefresh={load} />
      <Card>
        <p style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)" }}>
          Tasks auto-created by the Personal AI Trainer when persistent form issues are detected. Trainers respond via voice/text/video.
        </p>
      </Card>
      {data.tasks.length === 0 ? (
        <EmptyState
          title="No AI Trainer tasks yet"
          sub="Tasks will appear here as Phase 3 (Async trainer coaching) populates them."
        />
      ) : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Member</Th><Th>Issue</Th><Th>Status</Th><Th>Trainer</Th><Th>Created</Th></tr></thead>
            <tbody>
              {data.tasks.map((t) => (
                <tr key={t.task_id}>
                  <Td>{t.user_name || t.user_id}</Td>
                  <Td>{t.issue_summary || t.issue_type}</Td>
                  <Td><StatusBadge value={t.status} /></Td>
                  <Td>{t.assigned_trainer_name || (t.assigned_trainer_id ? "Assigned" : "Unassigned")}</Td>
                  <Td>{relTime(t.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}
